import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  UploadFile as UploadIcon,
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";

const BASE = (process.env.REACT_APP_BASE_API_URL || "http://localhost:8000/api/").replace(
  /\/$/,
  ""
);

const PDF_LIST = [
  { label: "Bus Route Map (Dubai)", filename: "BUS-ROUTE-MAP.pdf" },
  { label: "Driver Details (Dubai)", filename: "DRIVER-DETAILS.pdf" },
  //   { label: "Transport Form (Al Saraya)", filename: "transport-form-alsaraya.pdf" },
  //   { label: "Agreement Form (Al Saraya)", filename: "Alsaraya-PDS-Dubai-Transport-Agreement.pdf" },
  //   { label: "NBT Transport Agreement", filename: "nbt-transport-agreement.pdf" },
  //   { label: "Transport Form (Najmath)", filename: "transport-form-najmath.pdf" },
  //   { label: "Transport Form ASBT", filename: "Transport-form-ASBT.pdf" },
  //   { label: "Transport Form NBT", filename: "Transport-form-NBT.pdf" },
  //   { label: "Staff Transport Form ASBT", filename: "Transport-form-for-staff-ASBT.pdf" },
  //   { label: "Staff Transport Form NBT", filename: "Transport-form-for-staff-NBT.pdf" },
  //   { label: "Bus Routes DPS RAK", filename: "dps-rak-bus-routes.pdf" },
  //   { label: "Bus Routes Sharjah Senior", filename: "BUS-ROUTES-DPS-SHARJAH-senior.pdf" },
  //   { label: "Bus Routes Sharjah Primary", filename: "BUS-ROUTES-DPS-SHARJAH-primary.pdf" },
  //   { label: "Driver Details DPS Primary", filename: "DRIVER-DETAILS-DPS-PRIMARY.pdf" },
  //   { label: "Driver Details DPS Senior", filename: "DRIVER-DETAILS-DPS-SENIOR.pdf" },
  //   { label: "Bus Route 2023-2024 DPS RAK", filename: "BUS-ROUTE-2023-2024-DPS-RAK.pdf" },
];

const PDFManageTab = () => {
  const [loadingMap, setLoadingMap] = useState({});
  const [successMap, setSuccessMap] = useState({});
  const [existsMap, setExistsMap] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const fileInputRef = useRef(null);
  const currentFilename = useRef(null);

  // Check which PDFs already exist on server
  useEffect(() => {
    fetch(`${BASE}/pdf-status`)
      .then((r) => r.json())
      .then((data) => setExistsMap(data))
      .catch((err) => console.error("Could not fetch PDF status:", err));
  }, []);

  const handleButtonClick = (filename) => {
    currentFilename.current = filename;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    const targetFilename = currentFilename.current;
    if (!file || !targetFilename) return;

    if (file.type !== "application/pdf") {
      setSnackbar({ open: true, message: "Please select a PDF file only.", severity: "error" });
      return;
    }

    setLoadingMap((p) => ({ ...p, [targetFilename]: true }));
    setSuccessMap((p) => ({ ...p, [targetFilename]: false }));

    try {
      const formData = new FormData();
      formData.append("pdf", file);
      formData.append("targetFilename", targetFilename);

      const response = await fetch(`${BASE}/upload-pdf`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessMap((p) => ({ ...p, [targetFilename]: true }));
        setExistsMap((p) => ({ ...p, [targetFilename]: true }));
        setSnackbar({ open: true, message: `✅ ${targetFilename} uploaded!`, severity: "success" });
      } else {
        throw new Error(result.error || "Upload failed");
      }
    } catch (err) {
      setSnackbar({ open: true, message: `❌ ${err.message}`, severity: "error" });
    } finally {
      setLoadingMap((p) => ({ ...p, [targetFilename]: false }));
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          PDF File Manager
        </Typography>
        <Typography variant="body2" color="text.secondary">
          please select the the pdf file you want to upload. If the file already exists, it will be
          replaced with the new one.
        </Typography>
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        accept="application/pdf"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <Grid container spacing={3}>
        {PDF_LIST.map(({ label, filename }) => {
          const exists = existsMap[filename];
          const loading = loadingMap[filename];
          const success = successMap[filename];

          return (
            <Grid item xs={12} sm={6} md={4} key={filename}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: success ? "success.main" : exists ? "primary.main" : "warning.main",
                  borderWidth: 2,
                  transition: "border-color 0.3s",
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {label}
                  </Typography>

                  <Chip
                    label={filename}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.68rem", maxWidth: "100%", mb: 1 }}
                  />

                  {/* Status */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {exists ? (
                      <>
                        <CheckIcon fontSize="small" color="success" />
                        <Typography variant="caption" color="success.main">
                          File exists on server
                        </Typography>
                      </>
                    ) : (
                      <>
                        <MissingIcon fontSize="small" color="warning" />
                        <Typography variant="caption" color="warning.main">
                          Not uploaded yet
                        </Typography>
                      </>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                  {/* Upload / Replace */}
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    startIcon={
                      loading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />
                    }
                    onClick={() => handleButtonClick(filename)}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                  >
                    {loading ? "Uploading..." : exists ? "Replace PDF" : "Upload PDF"}
                  </Button>

                  {/* View — only if exists */}
                  {exists && (
                    <Tooltip title="View current PDF">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => window.open(`${BASE}/pdfs/${filename}`, "_blank")}
                        sx={{ textTransform: "none", minWidth: 60 }}
                      >
                        <ViewIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PDFManageTab;
